const NON_KEYBOARD_INPUT_TYPES = new Set([
  "radio",
  "checkbox",
  "button",
  "submit",
  "reset",
  "file",
  "hidden",
  "image",
  "range",
  "color",
]);

type ScrollableIonContent = HTMLElement & {
  scrollByPoint?: (x: number, y: number, duration: number) => Promise<void>;
};

/** Fields that can open the software keyboard (excludes radio/checkbox/etc.). */
export function isKeyboardTriggerField(element: HTMLElement | null): boolean {
  if (!element) {
    return false;
  }

  const tag = element.tagName;
  if (tag === "TEXTAREA" || tag === "ION-TEXTAREA") {
    return true;
  }
  if (element.getAttribute("contenteditable") === "true") {
    return true;
  }
  if (tag === "ION-INPUT") {
    return true;
  }
  if (tag !== "INPUT") {
    return false;
  }

  const type = ((element as HTMLInputElement).type || "text").toLowerCase();
  return !NON_KEYBOARD_INPUT_TYPES.has(type);
}

export function isFocusableFormField(element: HTMLElement | null): element is HTMLElement {
  return !!element && isKeyboardTriggerField(element);
}

export function shouldSkipKeyboardScroll(element: HTMLElement): boolean {
  return !!(
    element.closest(".message-input-wrapper") ||
    element.closest("app-messagerie-text-input")
  );
}

export function measureKeyboardHeight(fallback = 320): number {
  if (window.visualViewport) {
    const measured = window.innerHeight - window.visualViewport.height;
    if (measured > 50) {
      return measured;
    }
  }
  return fallback;
}

/**
 * Scrolls the focused field above the iOS keyboard using ion-content and any
 * nested overflow containers between the field and the page scroll root.
 */
export async function scrollFieldAboveKeyboard(
  element: HTMLElement,
  keyboardHeight: number,
  buffer = 32
): Promise<void> {
  if (!element || shouldSkipKeyboardScroll(element)) {
    return;
  }

  const rect = element.getBoundingClientRect();
  const visibleBottom = window.innerHeight - keyboardHeight - buffer;

  if (rect.top >= 0 && rect.bottom <= visibleBottom) {
    return;
  }

  const scrollDelta = Math.max(0, rect.bottom - visibleBottom + 16);
  if (scrollDelta <= 0) {
    return;
  }

  let node: HTMLElement | null = element.parentElement;
  while (node && node.tagName !== "ION-CONTENT") {
    const style = window.getComputedStyle(node);
    const canScroll =
      /(auto|scroll)/.test(style.overflowY) &&
      node.scrollHeight > node.clientHeight + 1;
    if (canScroll) {
      node.scrollTop += scrollDelta;
    }
    node = node.parentElement;
  }

  const ionContentEl = element.closest("ion-content") as ScrollableIonContent | null;
  if (ionContentEl?.scrollByPoint) {
    await ionContentEl.scrollByPoint(0, scrollDelta, 0);
    return;
  }

  const innerScroll = ionContentEl?.shadowRoot?.querySelector(
    ".inner-scroll"
  ) as HTMLElement | null;
  if (innerScroll) {
    innerScroll.scrollTop += scrollDelta;
  }
}

export function scheduleScrollFieldAboveKeyboard(
  element: HTMLElement,
  keyboardHeight: number,
  delaysMs: number[] = [50, 150, 300, 500]
): void {
  delaysMs.forEach((delay) => {
    setTimeout(() => {
      void scrollFieldAboveKeyboard(element, keyboardHeight);
    }, delay);
  });
}

export function scheduleScrollFocusedFieldAboveKeyboard(
  keyboardHeight: number,
  delaysMs: number[] = [50, 150, 300, 500]
): void {
  const active = document.activeElement as HTMLElement | null;
  if (!isFocusableFormField(active)) {
    return;
  }
  scheduleScrollFieldAboveKeyboard(active, keyboardHeight, delaysMs);
}
